// src/pages/MiInstitucionPage.js
//
// Portal mínimo de la institución para quien tiene un vínculo activo
// (SuperAdmin, Rector, Coordinador, Docente...) — ver TipoVinculoInstitucion
// en el backend. Esto es la base a la que llega el admin de una alianza
// después de loguearse (ver redirect en Auth.js).
//
// Deliberadamente mínimo por ahora: muestra la(s) institución(es) del
// usuario y su rol en cada una. El CRUD real (cursos, PEI, staff,
// pensiones) es la siguiente fase -- pendiente explícito, no construido
// todavía en esta versión.

import React, { useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Grid, Chip, Avatar,
  CircularProgress, Alert, Button, Divider,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import GroupsIcon from '@mui/icons-material/Groups';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PaymentsIcon from '@mui/icons-material/Payments';
import { meService } from '../services/api';

const ROL_LABEL = {
  SuperAdmin: 'Administrador de la alianza',
  Rector: 'Rector',
  Coordinador: 'Coordinador',
  Tesorero: 'Tesorero',
  Docente: 'Docente',
  DirectorGrupo: 'Director de grupo',
  Administrativo: 'Administrativo',
  Estudiante: 'Estudiante',
  Acudiente: 'Acudiente',
};

const SECCIONES_PROXIMAMENTE = [
  { icon: <MenuBookIcon />, title: 'Cursos', desc: 'Crear y administrar los cursos autorizados de la alianza.' },
  { icon: <GroupsIcon />, title: 'Staff', desc: 'Invitar coordinadores, docentes y demás equipo de la institución.' },
];

export default function MiInstitucionPage() {
  const nombreProducto = useTheme().productoInfo?.nombre || 'EducaT';
  const [instituciones, setInstituciones] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    meService.getMisInstituciones()
      .then(setInstituciones)
      .catch((e) => setError(e?.message || 'No se pudieron cargar tus instituciones.'));
  }, []);

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (instituciones === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (instituciones.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <SchoolIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          No perteneces a ninguna institución todavía
        </Typography>
        <Typography color="text.secondary">
          Si esperabas ver aquí tu institución, contacta a Fenagro para confirmar tu vinculación.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#1a4d2e' }}>
        Mi institución
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        {instituciones.length === 1
          ? `Portal de tu institución en la red ${nombreProducto}.`
          : `Perteneces a más de una institución en la red ${nombreProducto}.`}
      </Typography>

      <Grid container spacing={3}>
        {instituciones.map((inst) => (
          <Grid item xs={12} key={inst.id}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar src={inst.logo || undefined} sx={{ width: 56, height: 56, bgcolor: '#2e7d32' }}>
                  <SchoolIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{inst.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inst.municipality || 'Sin municipio registrado'}
                  </Typography>
                </Box>
                <Chip
                  label={ROL_LABEL[inst.role] || inst.role}
                  sx={{ bgcolor: '#e8f5e9', color: '#1a4d2e', fontWeight: 700 }}
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                {SECCIONES_PROXIMAMENTE.map((s) => (
                  <Grid item xs={12} sm={4} key={s.title}>
                    <Box sx={{
                      p: 2, borderRadius: 2, border: '1px dashed #ccc',
                      display: 'flex', gap: 1.5, alignItems: 'flex-start', opacity: 0.75,
                    }}>
                      <Box sx={{ color: '#2e7d32' }}>{s.icon}</Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                          {s.title} <Chip label="Próximamente" size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.65rem' }} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{s.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
                <Grid item xs={12} sm={4}>
                  <Box component={RouterLink} to="/mi-institucion/pensiones" sx={{
                    p: 2, borderRadius: 2, border: '1px solid #2e7d32', bgcolor: '#f0fdf0',
                    display: 'flex', gap: 1.5, alignItems: 'flex-start', textDecoration: 'none', color: 'inherit',
                    '&:hover': { borderColor: '#1a4d2e' },
                  }}>
                    <Box sx={{ color: '#2e7d32' }}><PaymentsIcon /></Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a4d2e' }}>Pensiones</Typography>
                      <Typography variant="caption" color="text.secondary">Ver y pagar cuotas de pensión.</Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Button component={RouterLink} to="/dashboard" variant="text" sx={{ color: '#2e7d32' }}>
          ← Ir a mi dashboard de estudiante
        </Button>
      </Box>
    </Container>
  );
}
